import React, { useState } from 'react';
import renderIf from 'render-if';
import classNames from 'classnames';
import { useController, ApplicationView as view } from '@aha-app/mvc';
import CustomFieldPreview from './CustomFieldPreview';
import FieldSetPreview from './FieldSetPreview';
import styles from './styles.module.css';
import type CardLayoutEditorController from '../controllers/CardLayoutEditorController';
import UiItemPreview from './UiItemPreview';

const FieldsSidebar = () => {
  const controller = useController<CardLayoutEditorController>();
  const {
    customFields,
    state: {
      showCustomFields,
      showStandardFields,
      showUiItems,
      visibleStandardFields,
      visibleCustomFields,
      visibleCustomFieldSets,
      visibleUiItems,
    },
  } = controller;

  // We still need to use useState because MVC state updates are batched, causing issues with <input>s
  const [searchText, setSearchText] = useState('');

  return (
    <div className={styles.sidebarPanel}>
      <div className='px-4 my-3'>
        {/* @ts-ignore */}
        <aha-search-field
          placeholder='Search'
          value={searchText}
          style={{ width: '100%' }}
          onInput={event => {
            const filterString = event.target.value;
            setSearchText(filterString);
            controller.actionSetSearchText(filterString);
          }}
        />
      </div>

      <div className={styles.fieldList}>
        <div className={classNames('pl-4 pr-2 my-3', styles.fieldListHeader)}>
          <h6 className='h-700'>Style fields</h6>
          <aha-button
            kind='plain'
            size='mini'
            onClick={() => controller.actionToggleShowUiItems()}
          >
            <i
              className={
                showUiItems
                  ? 'fa-regular fa-chevron-down'
                  : 'fa-regular fa-chevron-right'
              }
            />
          </aha-button>
        </div>

        <dl hidden={!showUiItems}>
          {visibleUiItems.map(uiItem => (
            <UiItemPreview key={uiItem.id} uiItem={uiItem} />
          ))}
        </dl>
      </div>

      <div className={styles.fieldList}>
        <div className={classNames('pl-4 pr-2 my-3', styles.fieldListHeader)}>
          <h6 className='h-700'>Standard fields</h6>
          <aha-button
            kind='plain'
            size='mini'
            onClick={() => controller.actionToggleShowStandardFields()}
          >
            <i
              className={
                showStandardFields
                  ? 'fa-regular fa-chevron-down'
                  : 'fa-regular fa-chevron-right'
              }
            />
          </aha-button>
        </div>

        <dl hidden={!showStandardFields}>
          {visibleStandardFields.map(({ fieldSet, fields }) => (
            <FieldSetPreview
              key={fieldSet.constructor.name}
              fieldSet={fieldSet}
              fields={fields}
            />
          ))}
        </dl>
      </div>

      {renderIf(customFields.length || visibleCustomFieldSets.length)(() => (
        <div className={styles.fieldList}>
          <div className={classNames('pl-4 pr-2 my-3', styles.fieldListHeader)}>
            <h6 className='h-700'>Custom fields</h6>
            <aha-button
              kind='plain'
              size='mini'
              onClick={() => controller.actionToggleShowCustomFields()}
            >
              <i
                className={
                  showCustomFields
                    ? 'fa-regular fa-chevron-down'
                    : 'fa-regular fa-chevron-right'
                }
              />
            </aha-button>
          </div>

          <dl hidden={!showCustomFields}>
            {visibleCustomFields.map(customField => (
              <CustomFieldPreview
                key={customField.key}
                customField={customField}
              />
            ))}
            {visibleCustomFieldSets.map(customFieldSet => {
              return (
                <FieldSetPreview
                  key={customFieldSet.recordFields.key}
                  fieldSet={customFieldSet}
                  fields={customFieldSet.fields}
                  customName={customFieldSet.recordFields.name}
                />
              );
            })}
          </dl>
        </div>
      ))}
    </div>
  );
};

export default view(FieldsSidebar);
